import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Hi! I am your assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const handleSend = () => {
    if (!input.trim()) return;
    setMessages([...messages, { sender: 'user', text: input }]);
    // Placeholder for LLM/RAG integration
    setTimeout(() => {
      setMessages(msgs => [...msgs, { sender: 'bot', text: 'This is a placeholder response. I will answer your question once connected to the LLM.' }]);
    }, 600);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <div className="fixed bottom-6 right-6 z-[1000]"> 
      {!open && (
        <button 
          className="bg-slate-500 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-700 text-white border-none rounded-full w-14 h-14 shadow-lg cursor-pointer flex items-center justify-center transition-colors duration-200"
          aria-label="Open chatbot" 
          onClick={() => setOpen(true)}
        >
          <MessageCircle size={28} />
        </button>
      )}
      {open && (
        <div className="w-[340px] h-[420px] bg-white/90 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-200 dark:border-slate-600 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-200 ease-out">
          <div className="bg-slate-500 dark:bg-slate-600 text-white px-4 py-3.5 font-semibold flex items-center justify-between">
            <span>Assistant</span>
            <button 
              className="bg-transparent border-none text-white cursor-pointer p-0 hover:opacity-80 transition-opacity" 
              aria-label="Close chatbot" 
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 p-4 overflow-y-auto bg-slate-50 dark:bg-slate-800 flex flex-col gap-2.5">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-[15px] leading-relaxed break-words ${
                  msg.sender === 'bot' 
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 self-start' 
                    : 'bg-slate-500 dark:bg-slate-600 text-white self-end'
                }`}
              >
                {msg.text}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="flex p-3 bg-gray-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-600">
            <input
              type="text"
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-lg px-3 py-2 text-[15px] outline-none mr-2 focus:border-slate-500 dark:focus:border-slate-500 placeholder:text-slate-400 dark:placeholder:text-slate-400"
            />
            <button 
              className="bg-slate-500 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-700 text-white border-none rounded-lg px-3 py-2 cursor-pointer transition-colors duration-200"
              onClick={handleSend}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotWidget;
